import { FIELD_DELIMITER } from 'js/utils/string';
import {
  CAPABILITY_QUESTIONS_PAGE_TYPE,
  KNOWN_PAGE_TYPES,
  STANDARD_QUESTIONS_PAGE_TYPE,
} from 'js/ydoc/docs/capabilities-assessment/page-types';

function capabilityReducer(selectedCapabilities) {
  function reduceCapability(acc, c) {
    if (c.id in selectedCapabilities) {
      acc.push(c);
    }
    c.children.forEach((child) => {
      reduceCapability(acc, child, selectedCapabilities);
    });
    return acc;
  }

  return reduceCapability;
}

export function getPageCapabilities(capabilitySet, selectedCapabilities) {
  return capabilitySet.capabilities.reduce(
    capabilityReducer(selectedCapabilities),
    []
  );
}

export function getAllCapabilities(document, capabilitySetCache) {
  const { pages = [] } = document;

  return pages.reduce((acc, page) => {
    if (page.type === 'capability-questions') {
      const capabilitySet = capabilitySetCache[page.capabilitySetId];
      return acc.concat(
        getPageCapabilities(capabilitySet, page.selectedCapabilities)
      );
    }
    return acc;
  }, []);
}

export function constructQuestionKey(capability, element) {
  return `${element.key}${FIELD_DELIMITER}${capability.id}`;
}

export const constructAdditionalElementKey = (
  page,
  capabilityElement,
  element
) => {
  return [page.guid, capabilityElement.key, element.key].join(FIELD_DELIMITER);
};

export function getQuestionId(question, capability, questionMap) {
  return questionMap[constructQuestionKey(capability, question)]?.id;
}

export { CAPABILITY_QUESTIONS_PAGE_TYPE, STANDARD_QUESTIONS_PAGE_TYPE };

/** Whether participants and results can show a page (see page-types). */
export function isKnownPage(page) {
  return KNOWN_PAGE_TYPES.includes(page?.type);
}

/** The assessment with only the pages participants can be shown. */
export function withKnownPages(assessment) {
  const pages = assessment?.document?.pages;

  if (!pages || pages.every(isKnownPage)) {
    return assessment;
  }

  return {
    ...assessment,
    document: { ...assessment.document, pages: pages.filter(isKnownPage) },
  };
}

/**
 * Results show standard pages together after the capability pages, each in
 * page order.
 */
export function groupPagesForResults(pages = []) {
  return {
    capabilityPages: pages.filter(
      (p) => p.type === CAPABILITY_QUESTIONS_PAGE_TYPE
    ),
    standardPages: pages.filter((p) => p.type === STANDARD_QUESTIONS_PAGE_TYPE),
  };
}

/**
 * Whether a page shows its title and description to participants. Pages from
 * before the setting existed show them.
 */
export function shouldRenderPageHeader(page) {
  return page?.renderHeader !== false;
}

/**
 * The name participants see for a page. Capability pages from before titles
 * were required fall back to their capability set's name.
 */
export function getPageTitle(page, capabilitySets = {}) {
  if (page.type === 'question-set') {
    return page.name;
  }

  if (page.type === STANDARD_QUESTIONS_PAGE_TYPE) {
    return page.title;
  }

  if (page.type === CAPABILITY_QUESTIONS_PAGE_TYPE) {
    return page.title || capabilitySets[page.capabilitySetId]?.name;
  }

  return page.type;
}
